import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, Settings, Plus, Trash2, Link2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';

const newMapping = () => ({ pattern: '', hsn_code: '', gst_rate: 18 });

const GstSettingsPage = () => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gst/settings`, { headers: authHeader() });
      setForm(data);
    } catch (e) { setErr(formatErr(e)); }
  }, [authHeader]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      const { _id: _i, created_at: _ca, updated_at: _ua, ...payload } = form || {};
      await axios.put(`${API}/gst/settings`, payload, { headers: authHeader() });
      setMsg('GST settings saved successfully.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (!form) {
    return <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={20} /> Loading…</div>;
  }

  const f = (key, label, type = 'text', placeholder = '', cls = '') => (
    <div className={cls}>
      <label className="block text-[11px] text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        value={form[key] || ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={inputCls}
        placeholder={placeholder}
        data-testid={`gst-set-${key}`}
      />
    </div>
  );

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full max-w-4xl" data-testid="gst-settings-page">
      <div className="flex items-center gap-2">
        <Settings className="text-teal-600" size={22} />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">GST Setup</h1>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}
      {msg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg p-3 text-sm">{msg}</div>}

      {/* Supplier identity */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Supplier (Your Business)</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {f('supplier_legal_name', 'Legal name *')}
          {f('supplier_trade_name', 'Trade name')}
          {f('supplier_gstin', 'GSTIN *', 'text', '15-character GSTIN')}
          {f('supplier_state', 'State', 'text', 'e.g. Karnataka')}
          {f('supplier_state_code', 'State code', 'text', 'e.g. 29 for KA')}
          {f('supplier_pincode', 'PIN code')}
          {f('supplier_phone', 'Phone')}
          {f('supplier_email', 'Email', 'email')}
        </div>
        <div className="mt-3">
          <label className="block text-[11px] text-slate-500 mb-1">Address</label>
          <textarea rows={3} value={form.supplier_address || ''} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} className={inputCls} placeholder="Registered business address (printed on invoices)" />
        </div>
      </div>

      {/* Numbering */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Invoice Numbering</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {f('invoice_prefix', 'Invoice prefix', 'text', 'UNS/GST')}
          {f('place_of_supply_default', 'Default place of supply', 'text', 'KA')}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Invoices auto-number per Indian FY (Apr–Mar): <span className="font-mono bg-slate-100 px-1 rounded">{form.invoice_prefix || 'UNS/GST'}/&lt;FY&gt;/&lt;0001&gt;</span>
        </p>
      </div>

      {/* Bank */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Bank Details (printed on invoice)</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {f('bank_name', 'Bank name')}
          {f('bank_account', 'Account number')}
          {f('bank_ifsc', 'IFSC code', 'text', 'e.g. HDFC0001234')}
          {f('bank_branch', 'Branch')}
        </div>
      </div>

      {/* HSN auto-mapping per line type */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Link2 size={14} className="text-teal-600" />
              HSN/SAC Auto-Mapping
            </h2>
            <p className="text-[11px] text-slate-500 mt-1">
              When a line item's description matches a <b>pattern</b> below, the GST invoice form will auto-fill the HSN/SAC and GST rate. Patterns use case-insensitive glob (<span className="font-mono bg-slate-100 px-1 rounded">*ppf*</span> matches "PPF install", "Full car PPF"). First match wins — put most-specific patterns at the top.
            </p>
          </div>
          <button
            onClick={() => setForm({ ...form, line_hsn_mappings: [...(form.line_hsn_mappings || []), newMapping()] })}
            className="text-xs bg-teal-100 hover:bg-teal-200 text-teal-800 font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 self-start"
            data-testid="gst-mapping-add-btn"
          >
            <Plus size={12} /> Add mapping
          </button>
        </div>
        {(form.line_hsn_mappings || []).length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg text-slate-400 text-sm border border-slate-200 border-dashed">
            No mappings yet. Click "Add mapping" to start.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 px-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <div className="col-span-5">Pattern</div>
              <div className="col-span-4">HSN/SAC code</div>
              <div className="col-span-2 text-right">GST %</div>
              <div className="col-span-1"></div>
            </div>
            {(form.line_hsn_mappings || []).map((m, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2" data-testid={`gst-mapping-row-${idx}`}>
                <input
                  type="text"
                  value={m.pattern}
                  onChange={(e) => {
                    const next = [...(form.line_hsn_mappings || [])];
                    next[idx] = { ...next[idx], pattern: e.target.value };
                    setForm({ ...form, line_hsn_mappings: next });
                  }}
                  className={`${inputCls} col-span-5 font-mono`}
                  placeholder="*ppf* or *ceramic*"
                  data-testid={`gst-mapping-pattern-${idx}`}
                />
                <input
                  type="text"
                  value={m.hsn_code}
                  onChange={(e) => {
                    const next = [...(form.line_hsn_mappings || [])];
                    next[idx] = { ...next[idx], hsn_code: e.target.value };
                    setForm({ ...form, line_hsn_mappings: next });
                  }}
                  className={`${inputCls} col-span-4 font-mono`}
                  placeholder="998719 / 39191000"
                  data-testid={`gst-mapping-hsn-${idx}`}
                />
                <select
                  value={m.gst_rate}
                  onChange={(e) => {
                    const next = [...(form.line_hsn_mappings || [])];
                    next[idx] = { ...next[idx], gst_rate: Number(e.target.value) };
                    setForm({ ...form, line_hsn_mappings: next });
                  }}
                  className={`${inputCls} col-span-2 text-right`}
                  data-testid={`gst-mapping-rate-${idx}`}
                >
                  {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
                <button
                  onClick={() => {
                    const next = (form.line_hsn_mappings || []).filter((_, i) => i !== idx);
                    setForm({ ...form, line_hsn_mappings: next });
                  }}
                  className="col-span-1 p-1.5 rounded hover:bg-red-50 text-red-500 self-center"
                  title="Remove mapping"
                  data-testid={`gst-mapping-del-${idx}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2" data-testid="gst-set-save">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save GST Setup'}
        </button>
      </div>
    </div>
  );
};

export default GstSettingsPage;
